import React from 'react'
import { IonIcon, IonRow, isPlatform } from '@ionic/react'
import classNames from 'classnames'

import { Button } from '@mytours/components'
import { ionHistory } from 'mobile_web/modules/navigation'
import { getDirMode, isRTL } from '../../../../platform'
import strings from '../../../../../strings'

import { chevronBack } from 'ionicons/icons'

import './styles.less'

interface Props {
	onBackButton?: React.MouseEventHandler<HTMLButtonElement>
	prevPagePath: string
	className?: string
	ariaLabel?: string
	icon?: string
	text: string
	children?: React.ReactNode
}

const BackButton: React.FC<Props> = ({
	onBackButton,
	className,
	ariaLabel = strings.accessibility_go_back,
	icon = chevronBack,
	text,
	prevPagePath,
}) => {
	// Without `onBackButton` this used to render `IonBackButton`, which pops Ionic's own nav stack
	// and falls back to a default href when there is nothing to pop. The library Button has no
	// equivalent, so both halves of that are wired explicitly: go back when there is somewhere to
	// go back to, otherwise follow the path the header worked out (`state.prevPagePath`, or the
	// tabs URL). Only two screens pass `onBackButton`, so this branch is the one most back buttons
	// in the app take.
	//
	// It also unifies the markup. `IonBackButton` took neither `className` nor the rtl/ios
	// modifiers and needed `--color: currentColor` to stop iOS colouring it differently; one
	// `<Button variant="clear">` for both branches makes that inconsistency go away rather than
	// reproducing it.
	const goBack = () => {
		if (ionHistory.length > 1) {
			ionHistory.goBack()
		} else {
			ionHistory.push(prevPagePath)
		}
	}

	return (
		<Button
			variant="clear"
			onClick={onBackButton ?? goBack}
			aria-label={ariaLabel}
			className={classNames('header-back-button', className, {
				rtl: isRTL(),
				ios: isPlatform('ios'),
			})}
		>
			<IonRow dir={getDirMode()}>
				{/* Ionic gives ion-icon `role="img"`, so axe wants alt text on it. The chevron is
				    decorative — the button is already named by aria-label — and the old wrapper hid
				    it by wrapping every child in aria-hidden. Hide the icon itself instead, which
				    is the same outcome without hiding the label text beside it. */}
				{icon ? <IonIcon icon={icon} aria-hidden="true" /> : null}
				{text}
			</IonRow>
		</Button>
	)
}

export default React.memo(BackButton)
